import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'event' | 'profile' | 'article';
  url?: string;
  canonicalUrl?: string;
  structuredData?: object;
  noindex?: boolean;
  nofollow?: boolean;
}

/**
 * SEO component for managing meta tags, Open Graph, Twitter Cards, and structured data
 */
const SEO: React.FC<SEOProps> = ({
  title = 'Quicket | Live Now!',
  description = 'Quicket is an event ticketing platform dedicated to simplifying event hosting for organizers and the ticket-buying experience for attendees.',
  image,
  type = 'website',
  url,
  canonicalUrl,
  structuredData,
  noindex = false,
  nofollow = false,
}) => {
  // Ensure image URL is absolute
  const getAbsoluteImageUrl = (imgUrl?: string): string | undefined => {
    if (!imgUrl) return undefined;
    if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) {
      return imgUrl;
    }
    // If relative URL, make it absolute using current origin
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${imgUrl.startsWith('/') ? imgUrl : `/${imgUrl}`}`;
    }
    return imgUrl;
  };

  const absoluteImageUrl = getAbsoluteImageUrl(image);
  const absoluteUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const canonical = canonicalUrl || absoluteUrl;

  // Build robots meta content
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  const robotsMeta = robotsContent.length > 0 ? robotsContent.join(', ') : undefined;

  // Update document title directly as well to ensure it updates immediately
  if (typeof window !== 'undefined') {
    document.title = title;
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {robotsMeta && <meta name="robots" content={robotsMeta} />}

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {absoluteUrl && <meta property="og:url" content={absoluteUrl} />}
      {absoluteImageUrl && <meta property="og:image" content={absoluteImageUrl} />}
      {absoluteImageUrl && <meta property="og:image:secure_url" content={absoluteImageUrl} />}
      {absoluteImageUrl && <meta property="og:image:type" content="image/jpeg" />}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={absoluteImageUrl ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {absoluteImageUrl && <meta name="twitter:image" content={absoluteImageUrl} />}

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

